import { can } from '@clim/client-core';
import { COMMISSION_STATUSES, COMMISSION_STATUS_LABELS, formatEuros, parseEurosToCents, type CommissionRecord, type CommissionSplitInfo, type CommissionStatus } from '@clim/domain';
import { useEffect, useMemo, useState } from 'react';
import { currentPeriod, EntryList, ExportButtons, PeriodPicker, Totals } from '../components/Commissions';
import { ErrorBox, Field, Input, Modal, Select, Textarea } from '../components/ui';
import { errorMessage } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { useTerritory } from '../lib/territory';
import { useOnline } from '../lib/use-online';

type Action = { to: CommissionStatus; title: string; needs: 'none' | 'comment' | 'reference' };
const ACTIONS: Action[] = [
  { to: 'validated', title: 'Valider', needs: 'none' },
  { to: 'paid', title: 'Marquer payé', needs: 'reference' },
  { to: 'to_validate', title: 'Remettre à valider', needs: 'comment' },
  { to: 'cancelled', title: 'Annuler', needs: 'comment' },
];

/**
 * Vue globale des commissions / rémunérations (§11) : filtrable par personne, territoire, période,
 * type et statut ; validation et paiement groupés, ajustements motivés (responsables).
 */
export function CommissionsTeamPage() {
  const engine = useEngine();
  const { me, api } = useReady();
  const online = useOnline();
  const { territoryId } = useTerritory();
  const [period, setPeriod] = useState(currentPeriod());
  const [person, setPerson] = useState('');
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [action, setAction] = useState<Action | null>(null);
  const [adjusting, setAdjusting] = useState(false);
  const [amending, setAmending] = useState<CommissionRecord | null>(null);
  const [splitting, setSplitting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const manage = can(me, 'commission.manage');
  const all = engine.list('commission').filter((c) => c.period === period && (!territoryId || c.territoryId === territoryId));
  const people = [...new Map(all.map((c) => [c.beneficiaryId, c.beneficiaryName])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const rows = all
    .filter((c) => (!person || c.beneficiaryId === person) && (!kind || c.kind === kind) && (!status || c.status === status))
    .sort((a, b) => a.beneficiaryName.localeCompare(b.beneficiaryName) || a.earnedOn.localeCompare(b.earnedOn));
  const byPerson = new Map<string, CommissionRecord[]>();
  for (const r of rows) byPerson.set(r.beneficiaryName, [...(byPerson.get(r.beneficiaryName) ?? []), r]);
  const toggle = (id: string) => setSelected((s) => (s.has(id) ? new Set([...s].filter((x) => x !== id)) : new Set([...s, id])));
  const chosen = rows.filter((r) => selected.has(r.id));
  const query = `${person ? `&beneficiaryId=${person}` : ''}${territoryId ? `&territoryId=${territoryId}` : ''}${kind ? `&kind=${kind}` : ''}${status ? `&status=${status}` : ''}`;

  return (
    <div>
      <div className="page-head">
        <h1>Commissions & rémunérations — équipe</h1>
        <PeriodPicker period={period} onChange={(p) => (setPeriod(p), setSelected(new Set()))} />
      </div>
      <div className="filters">
        <Select aria-label="Personne" value={person} onChange={(e) => setPerson(e.target.value)}>
          <option value="">Toutes les personnes</option>
          {people.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </Select>
        <Select aria-label="Type" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">Tous les types</option>
          <option value="sale">Ventes</option>
          <option value="install">Poses / entretiens</option>
          <option value="adjustment">Ajustements</option>
        </Select>
        <Select aria-label="Statut" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Tous les statuts</option>
          {COMMISSION_STATUSES.map((s) => (
            <option key={s} value={s}>
              {COMMISSION_STATUS_LABELS[s]}
            </option>
          ))}
        </Select>
      </div>
      <div className="card">
        <Totals entries={rows} />
        <ExportButtons period={period} query={query} />
      </div>
      {manage && (
        <div className="card sticky-actions">
          <div className="actions wrap">
            <span className="muted">
              {chosen.length} ligne(s) sélectionnée(s) · {formatEuros(chosen.reduce((s, c) => s + c.amountCents, 0))}
            </span>
            <button className="btn small" onClick={() => setSelected(new Set(rows.filter((r) => r.status === 'to_validate').map((r) => r.id)))}>
              Sélectionner les « à valider »
            </button>
            <button className="btn small" onClick={() => setSelected(new Set(rows.filter((r) => r.status === 'validated').map((r) => r.id)))}>
              Sélectionner les « validé »
            </button>
            {ACTIONS.map((a) => (
              <button key={a.to} className={`btn small${a.to === 'validated' || a.to === 'paid' ? ' primary' : ''}`} disabled={!chosen.length || !online} onClick={() => setAction(a)}>
                {a.title}
              </button>
            ))}
            <button
              className="btn small"
              disabled={chosen.length !== 1 || !online || chosen[0]?.status === 'cancelled'}
              title="Sélectionnez une seule ligne"
              onClick={() => setAmending(chosen[0]!)}
            >
              ✎ Modifier le montant
            </button>
            <button className="btn small" disabled={!online} onClick={() => setAdjusting(true)}>
              + Ajustement
            </button>
          </div>
          {!online && <p className="muted">Validation et paiement nécessitent une connexion.</p>}
          <ErrorBox message={error} />
        </div>
      )}
      {manage && (() => {
        // interventions réalisées à plusieurs : à répartir avant de pouvoir valider
        const crews = [...new Map(all.filter((c) => c.crewInterventionId && c.status === 'to_validate').map((c) => [c.crewInterventionId!, c])).values()];
        return crews.length ? (
          <div className="card legal-alert" data-testid="crew-to-split">
            <h2>Interventions à plusieurs techniciens : rémunération à répartir</h2>
            <p className="muted">Indiquez la part de chaque technicien (l’un peut toucher plus que l’autre), puis validez comme d’habitude.</p>
            <div className="actions wrap">
              {crews.map((c) => (
                <button key={c.crewInterventionId} className="btn small primary" disabled={!online} onClick={() => setSplitting(c.crewInterventionId)}>
                  Répartir — {c.dossierReference ?? ''} {c.clientName ?? ''}
                </button>
              ))}
            </div>
          </div>
        ) : null;
      })()}
      {[...byPerson.entries()].map(([name, list]) => (
        <div className="card" key={name}>
          <h2>
            {name}
            {list.find((c) => c.beneficiaryCompany)?.beneficiaryCompany && <span className="muted"> — 🏢 {list.find((c) => c.beneficiaryCompany)!.beneficiaryCompany}</span>}{' '}
            <span className="muted">· {formatEuros(list.filter((c) => c.status !== 'cancelled').reduce((s, c) => s + c.amountCents, 0))}</span>
          </h2>
          <EntryList entries={list} selectable={manage} selected={selected} onToggle={toggle} />
        </div>
      ))}
      {!rows.length && <p className="empty">Aucune ligne pour ces critères.</p>}
      {action && (
        <ActionDialog
          action={action}
          count={chosen.length}
          onClose={() => setAction(null)}
          onConfirm={async (extra) => {
            setError(null);
            try {
              await api.request('POST', '/v1/commissions/transitions', { ids: chosen.map((c) => c.id), to: action.to, ...extra });
              setSelected(new Set());
              await engine.sync();
            } catch (e) {
              setError(errorMessage(e));
              throw e;
            }
          }}
        />
      )}
      {amending && (
        <AmendDialog
          entry={amending}
          onClose={() => setAmending(null)}
          onSaved={() => {
            setSelected(new Set());
            void engine.sync();
          }}
        />
      )}
      {splitting && (
        <SplitDialog
          interventionId={splitting}
          onClose={() => setSplitting(null)}
          onSaved={() => {
            setSelected(new Set());
            void engine.sync();
          }}
        />
      )}
      {adjusting && <AdjustmentDialog people={people} period={period} onClose={() => setAdjusting(false)} onSaved={() => void engine.sync()} />}
    </div>
  );
}

function ActionDialog({ action, count, onClose, onConfirm }: { action: Action; count: number; onClose(): void; onConfirm(extra: Record<string, string>): Promise<void> }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal title={`${action.title} — ${count} ligne(s)`} onClose={onClose}>
      <div className="form">
        {action.needs === 'reference' && (
          <Field label="Référence du paiement (facultatif)" hint="n° de virement, n° de facture du sous-traitant…">
            <Input value={value} onChange={(e) => setValue(e.target.value)} />
          </Field>
        )}
        {action.needs === 'comment' && (
          <Field label="Motif (obligatoire)">
            <Textarea value={value} onChange={(e) => setValue(e.target.value)} />
          </Field>
        )}
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button
            className="btn primary"
            disabled={action.needs === 'comment' && value.trim().length < 3}
            onClick={() =>
              onConfirm(action.needs === 'reference' ? { paymentReference: value } : action.needs === 'comment' ? { comment: value } : {})
                .then(onClose)
                .catch((e) => setError(errorMessage(e)))
            }
          >
            Confirmer
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AdjustmentDialog({ people, period, onClose, onSaved }: { people: [string, string][]; period: string; onClose(): void; onSaved(): void }) {
  const { me, api } = useReady();
  const engine = useEngine();
  const { territoryId } = useTerritory();
  const [users, setUsers] = useState<[string, string, string[]][]>(people.map(([id, n]) => [id, n, []]));
  useEffect(() => {
    if (!can(me, 'user.read')) return;
    api.request<{ id: string; firstName: string; lastName: string; status: string; territoryIds: string[] }[]>('GET', '/v1/admin/users').then(
      (list) => setUsers(list.filter((u) => u.status === 'active').map((u) => [u.id, `${u.firstName} ${u.lastName}`, u.territoryIds])),
      () => undefined,
    );
  }, [api, me]);
  const territories = useMemo(() => engine.list('territory').filter((t) => t.isActive), [engine]);
  const [beneficiaryId, setBeneficiaryId] = useState('');
  const [terr, setTerr] = useState(territoryId ?? territories[0]?.id ?? '');
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [negative, setNegative] = useState(false);
  const [day, setDay] = useState(`${period}-${period === currentPeriod() ? String(new Date().getDate()).padStart(2, '0') : '01'}`);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cents = parseEurosToCents(amount);
  return (
    <Modal title="Ajustement (prime, reprise, correction)" onClose={onClose}>
      <div className="form">
        <Field label="Bénéficiaire">
          <Select value={beneficiaryId} onChange={(e) => setBeneficiaryId(e.target.value)}>
            <option value="">— choisir —</option>
            {users.sort((a, b) => a[1].localeCompare(b[1])).map(([id, n]) => (
              <option key={id} value={id}>
                {n}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid2">
          <Field label="Territoire">
            <Select value={terr} onChange={(e) => setTerr(e.target.value)}>
              {territories.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date">
            <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          </Field>
        </div>
        <Field label="Objet">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ex. Prime challenge, reprise sur dossier annulé…" />
        </Field>
        <div className="grid2">
          <Field label="Montant (€)">
            <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <label className="check">
            <input type="checkbox" checked={negative} onChange={(e) => setNegative(e.target.checked)} />
            Retenue (montant négatif)
          </label>
        </div>
        <Field label="Motif (obligatoire)">
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button
            className="btn primary"
            disabled={!beneficiaryId || !terr || !cents || label.trim().length < 2 || comment.trim().length < 3}
            onClick={() =>
              api
                .request('POST', '/v1/commissions/adjustments', { beneficiaryId, territoryId: terr, label, amountCents: negative ? -cents! : cents, earnedOn: day, comment })
                .then(() => {
                  onSaved();
                  onClose();
                })
                .catch((e) => setError(errorMessage(e)))
            }
          >
            Enregistrer {cents ? `${negative ? '−' : ''}${formatEuros(cents)}` : ''}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Administrateur : nouveau montant d'une ligne, même figée (« à valider » : remplacé ; validée / payée : correction). */
/** Répartition de la rémunération d'une intervention réalisée à plusieurs (montants libres, total = barème). */
function SplitDialog({ interventionId, onClose, onSaved }: { interventionId: string; onClose(): void; onSaved(): void }) {
  const { api } = useReady();
  const [info, setInfo] = useState<CommissionSplitInfo | null>(null);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.request<CommissionSplitInfo>('GET', `/v1/commissions/split/${interventionId}`).then(
      (r) => {
        setInfo(r);
        // proposition : parts égales (le reste des centimes au technicien principal), modifiable
        const n = r.technicians.length;
        const base = Math.floor(r.totalCents / n);
        setAmounts(
          Object.fromEntries(
            r.technicians.map((t, k) => [t.id, ((t.amountCents ?? base + (k === 0 ? r.totalCents - base * n : 0)) / 100).toFixed(2).replace('.', ',')]),
          ),
        );
      },
      (e) => setError(errorMessage(e)),
    );
  }, [api, interventionId]);
  const cents = (v: string | undefined) => Math.round(Number((v ?? '0').replace(',', '.').replace(/\s/g, '')) * 100);
  const sum = info ? info.technicians.reduce((s, t) => s + (cents(amounts[t.id]) || 0), 0) : 0;
  // répartition libre : si le total diffère du barème, un motif est demandé
  const differs = !!info && sum !== info.totalCents;
  const ok =
    !!info &&
    info.technicians.every((t) => Number.isFinite(cents(amounts[t.id])) && cents(amounts[t.id]) >= 0) &&
    (!differs || comment.trim().length >= 3);
  const save = () =>
    api
      .request('POST', '/v1/commissions/split', {
        interventionId,
        shares: info!.technicians.map((t) => ({ technicianId: t.id, amountCents: cents(amounts[t.id]) })),
        comment: comment || null,
      })
      .then(() => (onSaved(), onClose()), (e) => setError(errorMessage(e)));
  return (
    <Modal title="Répartir entre les techniciens" onClose={onClose}>
      <div className="form">
        {info && (
          <>
            <p>
              {info.dossierReference} {info.clientName} · total à répartir : <strong>{formatEuros(info.totalCents)}</strong>
              {info.split && <span className="muted"> (déjà réparti : vous pouvez corriger tant que rien n’est validé)</span>}
            </p>
            {info.technicians.map((t) => (
              <Field key={t.id} label={`${t.name}${t.main ? ' (technicien principal)' : ' (renfort)'} — €`}>
                <Input aria-label={`Part de ${t.name}`} inputMode="decimal" value={amounts[t.id] ?? ''} onChange={(e) => setAmounts({ ...amounts, [t.id]: e.target.value })} />
              </Field>
            ))}
            <p className={sum === info.totalCents ? 'muted' : 'legal-alert small'} role="status">
              Total saisi : {formatEuros(sum)}
              {sum !== info.totalCents
                ? ` — différent du barème (${formatEuros(info.totalCents)}) : écart ${formatEuros(sum - info.totalCents)}, motif obligatoire`
                : ' = barème ✓'}
            </p>
            <Field label={differs ? 'Motif (obligatoire : total différent du barème)' : 'Commentaire (facultatif)'}>
              <Input value={comment} onChange={(e) => setComment(e.target.value)} />
            </Field>
          </>
        )}
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Annuler
          </button>
          <button className="btn primary" disabled={!ok} onClick={() => void save()}>
            Enregistrer la répartition
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AmendDialog({ entry, onClose, onSaved }: { entry: CommissionRecord; onClose(): void; onSaved(): void }) {
  const { api } = useReady();
  const [amount, setAmount] = useState((entry.amountCents / 100).toFixed(2).replace('.', ','));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cents = Math.round(Number(amount.replace(',', '.').replace(/\s/g, '')) * 100);
  const save = () =>
    api
      .request('POST', `/v1/commissions/${entry.id}/amend`, { amountCents: cents, reason: reason.trim() })
      .then(() => (onSaved(), onClose()), (e) => setError(errorMessage(e)));
  return (
    <Modal title={`Modifier le montant — ${entry.beneficiaryName}`} onClose={onClose}>
      <div className="form">
        <p>
          {entry.label} · actuellement <strong>{formatEuros(entry.amountCents)}</strong>
        </p>
        <Field label="Nouveau montant (€)">
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Motif (obligatoire)">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <p className="muted">
          {entry.status === 'to_validate'
            ? 'La ligne prend ce montant (elle ne sera plus recalculée automatiquement).'
            : 'Ligne déjà validée ou payée : son historique est conservé, une ligne « Correction » ajoute la différence.'}
        </p>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Annuler
          </button>
          <button className="btn primary" disabled={!Number.isFinite(cents) || reason.trim().length < 3} onClick={() => void save()}>
            Enregistrer
          </button>
        </div>
      </div>
    </Modal>
  );
}
